import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Link,
  Stack,
  Typography,
  Popover,
  useTheme,
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import { setfeedbackPopup } from "context/features/stylesSlice";
import { wordpressURL } from "config";
import { logEvent } from "services/amplitude.service";
import PopupFeedback from "../popup/PopupFeedback";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import { useUserData } from "features/User";
import { staticsite } from "config";
import { useIsTab } from "hooks/is-tab.hook";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export default function Footer({ sx, isLandingPage = false }) {
  const { palette } = useTheme();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const { accountType } = useUserData();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleClick = (event, href, term) => {
    event.preventDefault();
    logEvent(`click_footer_${term}`, { [term]: "clicked", url: href });
    window.open(href, "_blank", "noopener noreferrer");
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (event) => {
    event.preventDefault();
    navigate("/profile/support");
    logEvent("click_footer_support", {
      support: "clicked",
      url: `${window.location.origin}/profile/support`,
    });
  };

  const open = Boolean(anchorEl);
  const isTab = useIsTab();
  const id = open ? "social-media-popover" : undefined;

  const group1Items = [
    {
      label: "Feedback",
      element: (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => {
            dispatch(setfeedbackPopup(true));
            logEvent("click_footer_feedback", { feedback: "clicked" });
          }}
        >
          Feedback
        </Link>
      ),
    },
    {
      label: "Privacy",
      element: (
        <Link
          href={`https://${staticsite}sooth.fyi/privacy-policy/`}
          target="_blank"
          underline="hover"
          onClick={(event) => {
            logEvent("click_footer_privacy", {
              feedback: "clicked",
              url: `https://${staticsite}sooth.fyi/privacy-policy/`,
            });
          }}
        >
          Privacy
        </Link>
      ),
    },
    {
      label: "Terms of Use",
      element: (
        <Link
          href={`https://${staticsite}sooth.fyi/terms-of-use/`}
          target="_blank"
          underline="hover"
          onClick={(event) => {
            logEvent("click_footer_terms", {
              feedback: "clicked",
              url: `https://${staticsite}sooth.fyi/terms-of-use/`,
            });
          }}
        >
          Terms of Use
        </Link>
      ),
    },
  ];

  if (accountType !== "trial") {
    group1Items.push({
      label: "Site Access Agreement",
      element: (
        <RouterLink
          to="/profile/subscription/subscriber-agreement"
          onClick={(event) => {
            logEvent("click_footer_site_agreement", {
              feedback: "clicked",
              url: `${window.location.origin}/profile/subscription/subscriber-agreement`,
            });
          }}
        >
          Site Access Agreement
        </RouterLink>
      ),
    });
  }

  const group2Items = [
    {
      label: "Lesson Plans",
      element: (
        <Link
          href="https://sooth.fyi/resources/educator-resources"
          underline="hover"
          target="_blank"
        >
          Lesson Plans
        </Link>
      ),
    },
    {
      label: "Help Videos",
      element: (
        <Link
          href="/profile/support"
          underline="hover"
          onClick={handleNavigation}
        >
          Help Videos
        </Link>
      ),
    },
    {
      label: "FAQ",
      element: (
        <Link
          href={`${wordpressURL}/faq/`}
          underline="hover"
          onClick={(e) => handleClick(e, `${wordpressURL}/faq/`, "faq")}
        >
          FAQ
        </Link>
      ),
    },
    {
      label: "Contact Us",
      element: (
        <Link
          href="mailto:contact@sooth.fyi"
          underline="hover"
          onClick={() =>
            logEvent("click_footer_email", {
              email: "clicked",
              email: "contact@sooth.fyi",
            })
          }
        >
          Contact Us
        </Link>
      ),
    },
    {
      label: "Follow",
      element: (
        <Link href="#" underline="hover" onClick={handlePopoverOpen}>
          Follow
        </Link>
      ),
    },
  ];

  const allItems = [...group1Items, ...group2Items];

  return (
    <Box
      component="footer"
      id="help-info-area"
      sx={{
        width: "100%",
        position: "static",
        py: isMobile ? 3 : 1,
        px: isMobile ? 1 : 6,
        ...(isMobile && { mb: 3 }),
        bgcolor: palette.background.default,
        ...(isLandingPage && { minHeight: "5vh" }),
        "@media print": {
          display: "none",
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          display: { xs: "none", sm: "none", lg: "grid" },
          gridTemplateAreas: `'group1 group2'`,
          gridTemplateColumns: "1fr 1fr",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            gridArea: "group1",
            display: "flex",
            flexDirection: "row",
            gap: 3,
            alignItems: "center",
          }}
        >
          {group1Items.map((item) => (
            <Box key={item.label} sx={{ textAlign: "center" }}>
              <Typography variant="body2" component="p">
                {item.element}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          id="help-info-area"
          sx={{
            gridArea: "group2",
            display: "flex",
            flexDirection: "row",
            gap: 3,
            alignItems: "center",
            justifyContent: "end",
          }}
        >
          {group2Items.map((item) => (
            <Box key={item.label} sx={{ textAlign: "center" }}>
              <Typography variant="body2" component="p">
                {item.element}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: "none", sm: "grid", lg: "none" },
          gridTemplateAreas: `
            "group1"
            "group2"
          `,
          gridTemplateRows: "auto auto",
          rowGap: 2,
        }}
      >
        <Box
          sx={{
            gridArea: "group1",
            display: "flex",
            flexDirection: "row",
            gap: 3,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {group1Items.map((item) => (
            <Box key={item.label} sx={{ textAlign: "center" }}>
              <Typography variant="body2" component="p">
                {item.element}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            gridArea: "group2",
            display: "flex",
            flexDirection: "row",
            gap: 3,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {group2Items.map((item) => (
            <Box key={item.label} sx={{ textAlign: "center" }}>
              <Typography variant="body2" component="p">
                {item.element}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: "grid", sm: "none", lg: "none" },
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
        }}
      >
        {allItems.map((item) => (
          <Box key={item.label} sx={{ textAlign: "center" }}>
            <Typography variant="body2" component="p">
              {item.element}
            </Typography>
          </Box>
        ))}
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Stack direction="column" spacing={1} sx={{ p: 2 }}>
          <AccessibleIconButton
            title="Twitter"
            onClick={(e) =>
              handleClick(e, "https://twitter.com/Sooth_fyi", "twitter")
            }
          >
            <TwitterIcon />
          </AccessibleIconButton>
          <AccessibleIconButton
            title="Facebook"
            onClick={(e) =>
              handleClick(e, "https://www.facebook.com/sooth.fyi", "facebook")
            }
          >
            <FacebookIcon />
          </AccessibleIconButton>
          <AccessibleIconButton
            title="Linkedin"
            onClick={(e) =>
              handleClick(
                e,
                "https://www.linkedin.com/company/sooth-fyi/",
                "linkedin"
              )
            }
          >
            <LinkedInIcon />
          </AccessibleIconButton>
          <AccessibleIconButton
            title="instagram"
            onClick={(e) =>
              handleClick(e, "https://instagram.com/sooth.fyi/", "instagram")
            }
          >
            <InstagramIcon />
          </AccessibleIconButton>
        </Stack>
      </Popover>

      <PopupFeedback />
      {/* <Box
        sx={{
          position: "absolute",
          right: 0,
          width: "50vw",
          height: "15vh",
          bottom: 0,
          zIndex: -1,
        }}
      /> */}
    </Box>
  );
}
